import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetActiveBankAccounts,
  GetBankAccounts,
  CreateBankAccount,
  UpdateBankAccount,
  DeleteBankAccount,
  GetBankTransactions,
  CreateBankTransaction,
} from "../Api";
import {
  DEFAULT_STALE_TIME,
  LOOKUP_STALE_TIME,
  ServiceQueryOptions,
  unwrapApiResponse,
} from "./query_helpers";

export const bankQueryKeys = {
  activeBankAccounts: () => ["activeBankAccounts"] as const,
  bankAccounts: (params?: any) => ["bankAccounts", params] as const,
  bankTransactions: (bankAccountId: string, params?: any) =>
    ["bankTransactions", bankAccountId, params] as const,
};

export const invalidateBankQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["activeBankAccounts"] });
  queryClient.invalidateQueries({ queryKey: ["bankAccounts"] });
  queryClient.invalidateQueries({ queryKey: ["bankTransactions"] });
};

export const useActiveBankAccountsQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: bankQueryKeys.activeBankAccounts(),
    queryFn: async () => unwrapApiResponse(await GetActiveBankAccounts()),
  });
};

export const useBankAccountsQuery = (
  params?: { page?: number; limit?: number; search?: string },
  options?: ServiceQueryOptions
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: bankQueryKeys.bankAccounts(params),
    queryFn: async () => unwrapApiResponse(await GetBankAccounts(params)),
  });
};

export const useCreateBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => unwrapApiResponse(await CreateBankAccount(data)),
    onSuccess: () => {
      invalidateBankQueries(queryClient);
    },
  });
};

export const useUpdateBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => unwrapApiResponse(await UpdateBankAccount(data)),
    onSuccess: () => {
      invalidateBankQueries(queryClient);
    },
  });
};

export const useDeleteBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => unwrapApiResponse(await DeleteBankAccount(id)),
    onSuccess: () => {
      invalidateBankQueries(queryClient);
    },
  });
};

export const useBankTransactionsQuery = (
  bank_account_id: string,
  params?: { page?: number; limit?: number },
  options?: ServiceQueryOptions
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    enabled: !!bank_account_id,
    ...options,
    queryKey: bankQueryKeys.bankTransactions(bank_account_id, params),
    queryFn: async () => unwrapApiResponse(await GetBankTransactions(bank_account_id, params)),
  });
};

export const useCreateBankTransactionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => unwrapApiResponse(await CreateBankTransaction(data)),
    onSuccess: (res, variables) => {
      invalidateBankQueries(queryClient);
      if (variables.bank_account_id) {
        queryClient.invalidateQueries({
          queryKey: ["bankTransactions", variables.bank_account_id],
        });
      }
    },
  });
};

export const useBankService = (config?: {
  fetchActiveBankAccountsEnabled?: boolean;
  fetchBankAccountsEnabled?: boolean;
  fetchBankAccountsParams?: { page?: number; limit?: number; search?: string };
}) => {
  const queryClient = useQueryClient();

  const activeBankAccountsQuery = useActiveBankAccountsQuery({
    enabled: config?.fetchActiveBankAccountsEnabled !== false,
  });

  const bankAccountsQuery = useBankAccountsQuery(config?.fetchBankAccountsParams, {
    enabled: !!config?.fetchBankAccountsEnabled,
  });

  const createBankAccountMutation = useCreateBankAccountMutation();
  const updateBankAccountMutation = useUpdateBankAccountMutation();
  const deleteBankAccountMutation = useDeleteBankAccountMutation();
  const createBankTransactionMutation = useCreateBankTransactionMutation();

  return {
    activeBankAccountsQuery,
    bankAccountsQuery,
    createBankAccountMutation,
    updateBankAccountMutation,
    deleteBankAccountMutation,
    createBankTransactionMutation,
  };
};
